import { useEffect, useMemo, useRef } from 'react';
import { useMiniappSdk } from 'app/shared/contexts/miniapp-sdk';
import { HEADER_BUTTON_MODE, LinkTypeEnum } from '@hqo/hqo-miniapp-client-sdk';
import { useSdkData } from 'app/shared/hooks/use-sdk-data/use-sdk-data.hook';

interface UseRichDescriptionStylesProps {
  richDescription?: string;
  showRichDescription?: boolean;
  onLinkClick?: (linkUrl: string) => void;
}

interface UseRichDescriptionStylesReturn {
  richDescriptionRef: React.RefObject<HTMLDivElement>;
}

export const useRichDescriptionStyles = ({
  richDescription,
  showRichDescription,
  onLinkClick,
}: UseRichDescriptionStylesProps): UseRichDescriptionStylesReturn => {
  const richDescriptionRef = useRef<HTMLDivElement>(null);
  const client = useMiniappSdk();
  const { availableCmds } = useSdkData();
  const hasOpenExternalLink = useMemo(() => availableCmds.includes('openExternalLink'), [availableCmds]);

  useEffect(() => {
    if (showRichDescription && richDescription && richDescriptionRef.current) {
      const uBodyElements = richDescriptionRef.current.querySelectorAll('.u_body');
      uBodyElements.forEach((element) => {
        const htmlElement = element as HTMLElement;
        htmlElement.style.minHeight = '0px';
        htmlElement.style.height = 'auto';
      });

      const uContainerElement = richDescriptionRef.current.querySelectorAll('.container');
      uContainerElement.forEach((element) => {
        const htmlElement = element as HTMLElement;
        htmlElement.style.maxWidth = '100%';
      });

      const uImageElement = richDescriptionRef.current.querySelectorAll('img');
      uImageElement.forEach((element) => {
        const htmlElement = element as HTMLElement;
        htmlElement.style.display = 'block';
        htmlElement.style.marginLeft = 'auto';
        htmlElement.style.marginRight = 'auto';
      });

      const uContentTextElements = richDescriptionRef.current.querySelectorAll('.u_content_text');
      uContentTextElements.forEach((element) => {
        const htmlElement = element as HTMLElement;
        htmlElement.style.padding = '10px 0 10px';
      });

      const uContentHeaderElements = richDescriptionRef.current.querySelectorAll('.u_content_heading');
      uContentHeaderElements.forEach((element) => {
        const htmlElement = element as HTMLElement;
        htmlElement.style.padding = '10px 0 10px';
      });

      const uContentAttachmentElements = richDescriptionRef.current.querySelectorAll('.u_content_custom_attachments_tool');
      uContentAttachmentElements.forEach((element) => {
        const htmlElement = element as HTMLElement;
        htmlElement.style.padding = '10px 0 10px';
      });

      const uContentVideoElements = richDescriptionRef.current.querySelectorAll('.u_content_video');
      uContentVideoElements.forEach((element) => {
        const htmlElement = element as HTMLElement;
        htmlElement.style.padding = '10px 0 10px';
      });

      const uContentDividerElements = richDescriptionRef.current.querySelectorAll('.u_content_divider');
      uContentDividerElements.forEach((element) => {
        const htmlElement = element as HTMLElement;
        htmlElement.style.padding = '10px 0 10px';
      });

      const uContentImageElements = richDescriptionRef.current.querySelectorAll('.u_content_image');
      uContentImageElements.forEach((element) => {
        const htmlElement = element as HTMLElement;
        htmlElement.style.padding = '10px 0 10px';
      });

      const imageElements = richDescriptionRef.current.querySelectorAll('img');
      imageElements.forEach((element) => {
        const htmlElement = element as HTMLElement;
        if (htmlElement.style.maxWidth === '480px') {
          htmlElement.style.maxWidth = '100%';
        }
      });

      const uContentTimerElements = richDescriptionRef.current.querySelectorAll('.u_content_timer');
      uContentTimerElements.forEach((element) => {
        const htmlElement = element as HTMLElement;
        htmlElement.style.padding = '10px 0 10px';
      });

      const uContentCustomEmbedElements = richDescriptionRef.current.querySelectorAll('.u_content_custom_embed_tool');
      uContentCustomEmbedElements.forEach((element) => {
        const htmlElement = element as HTMLElement;
        htmlElement.style.padding = '10px 0 10px';
      });

      const uContentSocialElements = richDescriptionRef.current.querySelectorAll('.u_content_social');
      uContentSocialElements.forEach((element) => {
        const htmlElement = element as HTMLElement;
        htmlElement.style.padding = '10px 0 10px';
      });

      const uContentLinkoutElements = richDescriptionRef.current.querySelectorAll('a');
      uContentLinkoutElements.forEach((element) => {
        const htmlElement = element as HTMLAnchorElement;
        htmlElement.target = '_blank';
        htmlElement.rel = 'noopener noreferrer';
        htmlElement.onclick = (e) => {
          onLinkClick?.(htmlElement.href);
          if (hasOpenExternalLink) {
            e.preventDefault();
            client?.openExternalLink(htmlElement.href);
          } else if (htmlElement.href.includes('tel') || htmlElement.href.includes('mailto')) {
            client?.redirectToContent({
              value: '',
              type: LinkTypeEnum.NATIVE_LINK,
              options: {
                url: htmlElement.href,
              },
            });
          } else {
            client?.header?.showHeader();
            client?.header?.setHeader('', HEADER_BUTTON_MODE.HISTORY);
          }
        };
      });
    }
  }, [showRichDescription, richDescription, client, hasOpenExternalLink, onLinkClick]);

  return { richDescriptionRef };
};
